import { useEffect, useState } from 'react'

/**
 * Transcend AirGap API type definitions
 * AirGap is Transcend's privacy governance and consent management solution
 */
declare global {
  interface Window {
    /**
     * Transcend UI API for opening consent manager
     */
    transcend?: {
      showConsentManager?: (options: { viewState: string }) => Promise<void>
    }

    /**
     * Transcend AirGap API for privacy governance and consent management
     */
    airgap?: {
      /**
       * Get the user's detected privacy regimes (e.g., GDPR, CCPA, USA)
       * @returns Set of regime identifiers
       */
      getRegimes?: () => Set<string>

      /**
       * Set user consent preferences
       * @param authEvent - Trusted browser event (required for consent validation)
       * @param consent - Consent preferences for different purposes
       */
      setConsent?: (
        authEvent: Event | unknown,
        consent: {
          Advertising?: boolean
          Analytics?: boolean
          [key: string]: boolean | undefined
        },
      ) => Promise<void>

      /**
       * Get current user consent state
       * @returns Current consent preferences and update status
       */
      getConsent?: () => {
        updated: boolean
        purposes?: {
          Analytics?: boolean
          Advertising?: boolean
          [key: string]: boolean | undefined
        }
      } | null

      /**
       * Check if user has opted in to tracking
       */
      isOptedIn?: () => boolean

      /**
       * Check if user has opted out of tracking
       */
      isOptedOut?: () => boolean

      /**
       * Wait for AirGap to be fully initialized
       */
      ready?: () => Promise<void>
    }
  }
}

export enum RegulationZone {
  GDPR = 'GDPR',
  USA = 'USA',
  Other = 'Other',
}

export const useTranscendAirGap = (): {
  isInitialized: boolean
  regulationZone: RegulationZone
} => {
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    setIsInitialized(!!window.airgap)
  }, [])

  // Default to GDRP if Transcend is not initialized for safety (assume GDPR compliance required)
  let regulationZone: RegulationZone = RegulationZone.GDPR

  if (isInitialized) {
    const regimes = window?.airgap?.getRegimes?.()

    if (regimes?.has('USA')) {
      regulationZone = RegulationZone.USA
    } else if (!regimes?.has('GDPR')) {
      regulationZone = RegulationZone.Other
    }
  }

  return {
    isInitialized,
    regulationZone,
  }
}
